// ** React Imports
import React, { useEffect, useState } from "react";

// ** Third Party Imports
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";

// ** Icons Imports
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// ** Store Imports
import { createGlossary } from "../../store/reducers/glossary/glossaryActions";
import { getCustomers } from "../../store/reducers/customers/customersActions";
import { getLanguages } from "../../store/reducers/language/languageActions";
import * as userTypes from "../../store/reducers/auth/userTypes";

const Glossaries = () => {
    // ** States
    const [customer, setCustomer] = useState("");
    const [file, setFile] = useState(null);
    const [type, setType] = useState("1");
    const [sourceLanguage, setSourceLanguage] = useState("");
    const [targetLanguage, setTargetLanguage] = useState("");
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(false);

    // ** Hooks
    const dispatch = useDispatch();
    const intl = useIntl();
    const customers = useSelector(state => state.customer_assing.customers);
    const allLanguages = useSelector(state => state.language.allLanguages);
    const userType = useSelector(state => (state.auth.user) ? state.auth.user.type : "invitado");

    useEffect(() => {
        dispatch(getLanguages());
        dispatch(getCustomers());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (customers.length > 0) setCustomer(customers[0].id)
    }, [customers]);

    useEffect(() => {
        if (allLanguages.length > 0) {
            const language = allLanguages.find(language => language.iso_code === "en")?.id ?? allLanguages[0].id;
            setSourceLanguage(language);
            setTargetLanguage(language);
        }
    }, [allLanguages]);

    const onSubmit = event => {
        event.preventDefault();
        setLoading(true);
        const form = new FormData();
        form.append("customer_id", customer);
        form.append("file", file);
        form.append("type", type);
        if (type === "1") {
            form.append("source_language", sourceLanguage);
            form.append("target_language", targetLanguage);
        } else if (type === "2") languages.map(language => form.append("languages[]", language.value));
        dispatch(createGlossary(form))
            .then(() => {
                toast.success("Glosario creado");

                const language = allLanguages.find(language => language.iso_code === "en")?.id ?? allLanguages[0].id;
                setCustomer(customers[0].id)
                setFile(null);
                setType("1");
                setSourceLanguage(language);
                setTargetLanguage(language);
                setLanguages([]);
            })
            .catch(() => null)
            .finally(() => setLoading(false));
    }

    return (
        <div className="box is-desktop">
            <h4 className="title has-text-primary is-4">
                <FormattedMessage id="menu.glossaries"/>
            </h4>
            {userType !== userTypes.IDISC_ADMIN ? (
                <>
                    <p>
                        <FormattedMessage id="glossaries.message"/>
                    </p>
                    <p className="mt-3">
                        <FormattedMessage id="glossaries.message_2"/>
                    </p>
                </>
            ) : (
                <>
                    <p>
                        <FormattedMessage id="glossaries.message_3"/>
                        {" "}
                        <a href="https://cloud.google.com/translate/docs/advanced/glossary#format-glossary">
                            https://cloud.google.com/translate/docs/advanced/glossary#format-glossary
                        </a>
                    </p>
                    <hr/>
                    <form onSubmit={onSubmit}>
                        <div className="columns is-desktop">
                            <div className="column">
                                <div className="field">
                                    <label className="label">
                                        <FormattedMessage id="general.customer"/>
                                    </label>
                                    <div className="select is-fullwidth">
                                        <select onChange={event => setCustomer(event.target.value)} value={customer}>
                                            {customers?.map(customer => (
                                                <option key={customer.id} value={customer.id}>
                                                    {customer.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="columns is-desktop">
                            <div className="column">
                                <div className="field">
                                    <div className="file">
                                        <label className="file-label">
                                            <input
                                                accept=".csv"
                                                className="file-input"
                                                onChange={event => setFile(event.target.files[0])}
                                                type="file"
                                            />
                                            <span className="file-cta">
                                                <span className="file-icon">
                                                    <FontAwesomeIcon icon={faUpload}/>
                                                </span>
                                                <span className="file-label">
                                                    <FormattedMessage id="glossaries.file"/>
                                                </span>
                                            </span>
                                            {file && (
                                                <span className="file-name">{file.name}</span>
                                            )}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="columns is-desktop">
                            <div className="column">
                                <div className="field">
                                    <div className="control">
                                        <label className="radio">
                                            <input
                                                checked={type === "1"}
                                                className="mr-1"
                                                name="type"
                                                onChange={event => setType(event.target.value)}
                                                type="radio"
                                                value="1"
                                            />
                                            <FormattedMessage id="glossaries.unidirectional"/>
                                        </label>
                                        <label className="radio">
                                            <input
                                                checked={type === "2"}
                                                className="mr-1"
                                                name="type"
                                                onChange={event => setType(event.target.value)}
                                                type="radio"
                                                value="2"
                                            />
                                            <FormattedMessage id="glossaries.equivalent_term"/>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {type === "1" ? (
                            <div className="columns is-desktop">
                                <div className="column">
                                    <div className="field">
                                        <label className="label">
                                            <FormattedMessage id="customer.assignLangSource"/>
                                        </label>
                                        <div className="select is-fullwidth">
                                            <select
                                                onChange={event => setSourceLanguage(event.target.value)}
                                                value={sourceLanguage}
                                            >
                                                {allLanguages?.map(language => (
                                                    <option key={language.id} value={language.id}>
                                                        {intl.formatMessage({id: `language.${language.iso_code}`})}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="column">
                                    <div className="field">
                                        <label className="label">
                                            <FormattedMessage id="customer.assignLangTarget"/>
                                        </label>
                                        <div className="select is-fullwidth">
                                            <select
                                                onChange={event => setTargetLanguage(event.target.value)}
                                                value={targetLanguage}
                                            >
                                                {allLanguages?.map(language => (
                                                    <option key={language.id} value={language.id}>
                                                        {intl.formatMessage({id: `language.${language.iso_code}`})}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : type === "2" && (
                            <div className="columns is-desktop">
                                <div className="column">
                                    <div className="field">
                                        <label className="label">
                                            <FormattedMessage id="language.languages"/>
                                        </label>
                                        <Select
                                            isMulti
                                            onChange={setLanguages}
                                            options={allLanguages.map(language => ({
                                                label: intl.formatMessage({id: `language.${language.iso_code}`}),
                                                value: language.id
                                            }))}
                                            placeholder=""
                                            value={languages}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="columns">
                            <div className="is-flex column is-3">
                                <button className={`button is-info${loading ? " is-loading" : ""}`} type="submit">
                                    <FormattedMessage id="general.save"/>
                                </button>
                            </div>
                        </div>
                    </form>
                </>
            )}
        </div>
    );
}

export default Glossaries;
